import { useState } from 'react';
import { Eye, Trash2 } from 'lucide-react';
import { Badge } from '../../../ui/badge';
import { Modal } from '../../../ui/Modal';
import { DeleteConfirmModal } from '../../../ui/DeleteConfirmModal';

interface Enquiry {
  id: number;
  name: string;
  email: string;
  subject: string;
  date: string;
  status: 'New' | 'Replied' | 'Closed';
}

const mockEnquiries: Enquiry[] = [
  {
    id: 1,
    name: 'John Smith',
    email: 'john.smith@example.com',
    subject: 'Partnership Opportunity',
    date: 'Jan 15, 2026',
    status: 'New',
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@company.com',
    subject: 'Project Consultation',
    date: 'Jan 14, 2026',
    status: 'Replied',
  },
  {
    id: 3,
    name: 'Michael Chen',
    email: 'mchen@tech.io',
    subject: 'Technical Support',
    date: 'Jan 13, 2026',
    status: 'Closed',
  },
  {
    id: 4,
    name: 'Emma Wilson',
    email: 'emma.w@startup.com',
    subject: 'Service Inquiry',
    date: 'Jan 12, 2026',
    status: 'New',
  },
  {
    id: 5,
    name: 'David Lee',
    email: 'david.lee@business.com',
    subject: 'Portfolio Review',
    date: 'Jan 11, 2026',
    status: 'Replied',
  },
];

export function EnquiriesTable() {
  const [viewEnquiry, setViewEnquiry] = useState<Enquiry | null>(null);
  const [deleteEnquiry, setDeleteEnquiry] = useState<Enquiry | null>(null);

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'New':
        return 'bg-blue-100 text-blue-700 hover:bg-blue-100';
      case 'Replied':
        return 'bg-green-100 text-green-700 hover:bg-green-100';
      case 'Closed':
        return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
      default:
        return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
    }
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Enquiries</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-y border-gray-100">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Name
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                  Email
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                  Subject
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                  Date
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mockEnquiries.map((enquiry) => (
                <tr
                  key={enquiry.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{enquiry.name}</p>
                    <p className="text-xs text-gray-500 md:hidden mt-1">{enquiry.email}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell">
                    {enquiry.email}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 hidden lg:table-cell">
                    {enquiry.subject}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 hidden sm:table-cell">
                    {enquiry.date}
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={getStatusVariant(enquiry.status)}>
                      {enquiry.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewEnquiry(enquiry)}
                        className="p-2 hover:bg-blue-50 rounded-xl transition-all text-blue-600 active:scale-95"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteEnquiry(enquiry)}
                        className="p-2 hover:bg-red-50 rounded-xl transition-all text-red-600 active:scale-95"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Modal */}
      {viewEnquiry && (
        <Modal
          isOpen={!!viewEnquiry}
          onClose={() => setViewEnquiry(null)}
          title="Enquiry Details"
          size="md"
        >
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Name
                </label>
                <p className="text-sm text-gray-900 mt-2 font-medium">{viewEnquiry.name}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Email
                </label>
                <p className="text-sm text-gray-900 mt-2">{viewEnquiry.email}</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Subject
              </label>
              <p className="text-sm text-gray-900 mt-2 font-medium">{viewEnquiry.subject}</p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Date
                </label>
                <p className="text-sm text-gray-900 mt-2">{viewEnquiry.date}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </label>
                <div className="mt-2">
                  <Badge className={getStatusVariant(viewEnquiry.status)}>
                    {viewEnquiry.status}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                onClick={() => setViewEnquiry(null)}
                className="px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteEnquiry && (
        <DeleteConfirmModal
          isOpen={!!deleteEnquiry}
          onClose={() => setDeleteEnquiry(null)}
          onConfirm={() => console.log('Delete enquiry:', deleteEnquiry.id)}
          itemName={deleteEnquiry.name}
          itemType="enquiry"
        />
      )}
    </>
  );
}
